<template>
  <div class="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full space-y-8">
      <div class="text-center">
        <h2 class="mt-6 text-3xl font-extrabold text-gray-900">Sign in to your account</h2>
        <p class="mt-2 text-sm text-gray-600">
          Or
          <router-link
            to="/auth/register"
            class="font-medium text-blue-600 hover:text-blue-500 transition-colors"
          >
            create a new account
          </router-link>
        </p>
      </div>

      <!-- Notification for verification required -->
      <div v-if="verificationAlertVisible" class="rounded-md bg-yellow-50 p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path
                fill-rule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-yellow-800">Email verification required</h3>
            <div class="mt-2 text-sm text-yellow-700">
              <p>
                Your email address needs to be verified. Please check your inbox or
                <router-link to="/auth/verify-email" class="font-medium underline">
                  verify your email now </router-link
                >.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Notification for existing user trying to login -->
      <div v-if="existingUserAlertVisible" class="rounded-md bg-blue-50 p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-blue-800">Account verification needed</h3>
            <div class="mt-2 text-sm text-blue-700">
              <p>
                We found your account! Please verify your email address to complete your account
                setup and sign in.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Account disabled in Keycloak. Reason discriminates the banner
           palette + per-status CTA. -->
      <div
        v-if="disabled"
        class="rounded-md border p-4"
        :class="disabledStyles[disabled.status].box"
      >
        <h3 class="text-sm font-semibold" :class="disabledStyles[disabled.status].title">
          {{ disabledStyles[disabled.status].heading }}
        </h3>
        <p class="mt-1 text-sm" :class="disabledStyles[disabled.status].body">
          {{ disabledStyles[disabled.status].body_text }}
        </p>
        <a
          v-if="disabled.status === 'pending_approval' && disabled.waitlist_url"
          :href="disabled.waitlist_url"
          target="_blank"
          rel="noopener"
          class="mt-3 inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700"
        >
          Tell us about your use case ↗
        </a>
        <a
          v-else-if="disabled.status === 'suspended'"
          href="/app/billing"
          class="mt-3 inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium rounded-md bg-orange-600 text-white hover:bg-orange-700"
        >
          Open billing →
        </a>
        <a
          v-else-if="disabled.support_email"
          :href="`mailto:${disabled.support_email}?subject=Account%20${disabled.status}`"
          class="mt-3 inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium rounded-md bg-gray-700 text-white hover:bg-gray-800"
        >
          Contact support
        </a>
      </div>

      <div class="mt-8">
        <LoginForm
          @success="handleLoginSuccess"
          @error="handleLoginError"
          @forgot-password="router.push('/auth/forgot-password')"
          @register="router.push('/auth/register')"
          @verification-needed="handleVerificationNeeded"
          @account-disabled="handleAccountDisabled"
        />
      </div>

      <div class="text-center">
        <router-link
          to="/auth/forgot-password"
          class="text-sm text-blue-600 hover:text-blue-500 transition-colors"
        >
          Forgot your password?
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onMounted, ref } from 'vue';
  import { useRouter } from 'vue-router';
  import { useAuth } from '@/features/auth/composables/useAuth';
  import { LoginForm } from '@/features/auth/components';
  import { useOrganizationsStore } from '@/stores/organizations';

  const router = useRouter();
  const { isAuthenticated, isVerified } = useAuth();
  const orgStore = useOrganizationsStore();
  const verificationAlertVisible = ref(false);

  type DisabledStatus = 'pending_approval' | 'banned' | 'suspended' | 'deactivated' | 'disabled';
  interface DisabledInfo {
    status: DisabledStatus;
    message: string;
    waitlist_url: string;
    support_email: string;
  }
  const disabled = ref<DisabledInfo | null>(null);

  const handleAccountDisabled = (info: DisabledInfo) => {
    disabled.value = info;
  };

  // Per-status banner copy + palette. FE owns the wording so we never
  // surface raw BE prose to the user. Pending = amber (action awaited);
  // banned = red (terminal); suspended = orange (recoverable);
  // deactivated/disabled = neutral gray.
  interface DisabledStyle {
    box: string;
    title: string;
    body: string;
    heading: string;
    body_text: string;
  }
  const disabledStyles: Record<DisabledStatus, DisabledStyle> = {
    pending_approval: {
      box:       'bg-amber-50 border-amber-200',
      title:     'text-amber-900',
      body:      'text-amber-800',
      heading:   'Account pending approval',
      body_text: "Your account is awaiting review. We'll email you once it's enabled — usually within 1–3 business days. If you haven't already, share your use case via the form below to speed things up.",
    },
    banned: {
      box:       'bg-red-50 border-red-200',
      title:     'text-red-900',
      body:      'text-red-800',
      heading:   'Account suspended',
      body_text: 'This account has been suspended. Contact support if you believe this is in error.',
    },
    suspended: {
      box:       'bg-orange-50 border-orange-200',
      title:     'text-orange-900',
      body:      'text-orange-800',
      heading:   'Account suspended',
      body_text: 'Your account is on hold. Resolve outstanding billing or contact support to restore access.',
    },
    deactivated: {
      box:       'bg-gray-50 border-gray-200',
      title:     'text-gray-900',
      body:      'text-gray-700',
      heading:   'Account deactivated',
      body_text: 'This account has been deactivated. Contact support to reactivate.',
    },
    disabled: {
      box:       'bg-gray-50 border-gray-200',
      title:     'text-gray-900',
      body:      'text-gray-700',
      heading:   'Account unavailable',
      body_text: 'This account is currently unavailable. Contact support for help.',
    },
  };
  const existingUserAlertVisible = ref(false);

  // Handle successful login
  const handleLoginSuccess = async (user: unknown) => {
    // If the user is not verified, show the verification alert
    const userData = user as { is_verified?: boolean };
    if (userData && userData.is_verified === false) {
      verificationAlertVisible.value = true;
    } else {
      // Initialize organizations
      await orgStore.initialize();

      // Redirect to app on successful login
      router.push('/app');
    }
  };

  // Handle login error
  const handleLoginError = (error: string) => {
    // Check if the error message indicates verification is needed
    if (error.toLowerCase().includes('verification') || error.toLowerCase().includes('verify')) {
      // Show verification alert for standard verification errors
      if (error.includes('complete your account setup')) {
        existingUserAlertVisible.value = true;
      } else {
        verificationAlertVisible.value = true;
      }
    }
  };

  // Handle verification needed
  const handleVerificationNeeded = (email: string) => {
    // Show appropriate alert based on the context
    existingUserAlertVisible.value = true;

    // Redirect to verification page with email after a brief delay
    setTimeout(() => {
      router.push({
        path: '/auth/verify-email',
        query: { email },
      });
    }, 2000);
  };

  // Redirect if already authenticated and verified
  onMounted(() => {
    if (isAuthenticated.value && isVerified.value) {
      router.push('/app');
    }
  });
</script>
